import { ApiRequestError } from './api-request.util';

/**
 * Stable `errorCode` values returned by the backend on OTP verification failure.
 * See apps/documentation/docs/backend/auth/otp-error-handling.md.
 */
export const OTP_ERROR_CODES = {
  INVALID: 'OTP_INVALID',
  EXPIRED: 'OTP_EXPIRED',
  ALREADY_USED: 'OTP_ALREADY_USED',
  TOO_MANY_ATTEMPTS: 'OTP_TOO_MANY_ATTEMPTS',
} as const;

/** Distinct OTP failures a client needs to show different copy for. */
export type OtpFailureReason =
  | 'invalid'
  | 'expired'
  | 'alreadyUsed'
  | 'tooManyAttempts'
  | 'deactivated'
  | 'rateLimit';

const REASON_BY_ERROR_CODE: Record<string, OtpFailureReason> = {
  [OTP_ERROR_CODES.INVALID]: 'invalid',
  [OTP_ERROR_CODES.EXPIRED]: 'expired',
  [OTP_ERROR_CODES.ALREADY_USED]: 'alreadyUsed',
  [OTP_ERROR_CODES.TOO_MANY_ATTEMPTS]: 'tooManyAttempts',
  ACCOUNT_BLOCKED: 'deactivated',
};

/**
 * Falls back to message matching for responses without an `errorCode`, which
 * covers deactivated/blocked accounts and the resend cooldown.
 */
function reasonFromMessage(message: string): OtpFailureReason {
  const normalized = message.toLowerCase();

  if (normalized.includes('deactivated') || normalized.includes('blocked')) {
    return 'deactivated';
  }
  if (normalized.includes('wait')) {
    return 'rateLimit';
  }
  return 'invalid';
}

const OTP_FAILURE_REASONS = new Set<OtpFailureReason>([
  'invalid',
  'expired',
  'alreadyUsed',
  'tooManyAttempts',
  'deactivated',
  'rateLimit',
]);

/**
 * Reads a typed `reason` from errors such as mobile `PhoneOtpError`, which
 * carry the same failure vocabulary as backend `errorCode`s.
 */
function reasonFromTaggedError(error: unknown): OtpFailureReason | null {
  if (!(error instanceof Error) || !('reason' in error)) {
    return null;
  }

  const reason = (error as { reason: unknown }).reason;
  return typeof reason === 'string' &&
    OTP_FAILURE_REASONS.has(reason as OtpFailureReason)
    ? (reason as OtpFailureReason)
    : null;
}

export function resolveOtpFailureReason(error: unknown): OtpFailureReason {
  const tagged = reasonFromTaggedError(error);
  if (tagged) {
    return tagged;
  }

  if (!(error instanceof ApiRequestError)) {
    return 'invalid';
  }

  if (error.status === 429) {
    return 'tooManyAttempts';
  }

  const mapped = error.errorCode
    ? REASON_BY_ERROR_CODE[error.errorCode]
    : undefined;

  return mapped ?? reasonFromMessage(error.message);
}
